// src/components/PresenceGate.js
import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { auth, rtdb } from "../firebase";
import {
  ref,
  set,
  onDisconnect,
  serverTimestamp,
  onValue,
} from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";
import {
  getNativeAppForegroundStatus,
  addNativeAppForegroundChangeListener,
} from "../plugins/NativeAppForeground";

// Phase N9B: on Capacitor Android, document.visibilitychange fires
// whenever ANY Activity occludes MainActivity's WebView - including a
// same-app native screen, not just genuine backgrounding - so it is not a
// trustworthy inApp signal there. The native, process-wide foreground
// tracker (LivoraApplication) is authoritative instead. Ordinary web
// browsers (and any platform without the native plugin) keep the existing
// document-visibility behavior unchanged.
function isNativeAndroidRuntime() {
  try {
    return Capacitor.isNativePlatform() === true && Capacitor.getPlatform() === "android";
  } catch {
    return false;
  }
}

function isPresenceBlocked() {
  const blockedAt = Number(sessionStorage.getItem("presence_blocked_at") || "0");
  const blocked = sessionStorage.getItem("presence_blocked") === "1";
  const now = Date.now();

  if (blocked && (!blockedAt || now - blockedAt > 10_000)) {
    sessionStorage.removeItem("presence_blocked");
    sessionStorage.removeItem("presence_blocked_at");
  }

  return sessionStorage.getItem("presence_blocked") === "1";
}

export default function PresenceGate() {
  const cleanupRef = useRef(null);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      if (!user) return;

      if (isPresenceBlocked()) return;

      const uid = user.uid;
      const statusRef = ref(rtdb, `status/${uid}`);
      const connectedRef = ref(rtdb, ".info/connected");
      const nativeAndroid = isNativeAndroidRuntime();

      let rtdbConnected = false;
      let hadConnectedOnce = false;
      let establishing = false;
      // Phase N9B: on Capacitor Android this is kept in sync with the
      // native, process-wide foreground signal (seeded below, then updated
      // by the listener) - it is what "currently visible" means for
      // establishPresence's initial write. On other platforms it is unused
      // (document.visibilityState is read directly instead).
      let nativeForegroundState = true;

      const currentVisible = () =>
        nativeAndroid ? nativeForegroundState : document.visibilityState === "visible";

      const writePresence = async (visible, reason) => {
        if (isPresenceBlocked()) return;
        await set(statusRef, {
          online: true,
          state: "online",
          inApp: visible === true,
          lastChanged: serverTimestamp(),
          ...(reason ? { reason } : {}),
        });
      };

      const registerOnDisconnect = async () => {
        await onDisconnect(statusRef).set({
          online: false,
          state: "offline",
          inApp: false,
          lastChanged: serverTimestamp(),
          reason: "disconnect",
        });
      };

      const establishPresence = async (reason) => {
        if (isPresenceBlocked() || establishing) return;
        establishing = true;
        try {
          await registerOnDisconnect();
          await writePresence(currentVisible(), reason);
        } catch (e) {
          console.warn("presence establish failed:", e?.message || e);
        } finally {
          establishing = false;
        }
      };

      const unsubConnected = onValue(connectedRef, (snap) => {
        const nowConnected = snap.val() === true;

        if (!nowConnected) {
          rtdbConnected = false;
          return;
        }

        if (rtdbConnected) return;

        rtdbConnected = true;
        const reason = hadConnectedOnce ? "reconnect" : "boot";
        hadConnectedOnce = true;
        void establishPresence(reason);
      });

      let onVis = null;
      let onPageHide = null;
      let unsubNativeForeground = null;

      if (nativeAndroid) {
        // Seed synchronously (best-effort) before any write depends on it -
        // the default above (true) already matches reality if this races.
        getNativeAppForegroundStatus().then((status) => {
          nativeForegroundState = status.isForeground !== false;
        });

        unsubNativeForeground = addNativeAppForegroundChangeListener((isForeground) => {
          nativeForegroundState = isForeground === true;
          if (isPresenceBlocked() || !rtdbConnected) return;
          void writePresence(
            nativeForegroundState,
            nativeForegroundState ? "native_foreground" : "native_background"
          );
        });
      } else {
        onVis = () => {
          if (isPresenceBlocked() || !rtdbConnected) return;
          void writePresence(
            document.visibilityState === "visible",
            "visibility"
          );
        };

        onPageHide = () => {
          if (isPresenceBlocked() || !rtdbConnected) return;
          void writePresence(false, "pagehide");
        };

        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("pagehide", onPageHide);
      }

      cleanupRef.current = () => {
        unsubConnected();
        if (nativeAndroid) {
          void unsubNativeForeground?.remove?.();
        } else {
          document.removeEventListener("visibilitychange", onVis);
          window.removeEventListener("pagehide", onPageHide);
        }
      };
    });

    return () => {
      if (cleanupRef.current) cleanupRef.current();
      unsubAuth();
    };
  }, []);

  return null;
}
